import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Users, CalendarDays, TrendingUp, TrendingDown, Monitor, Smartphone, Tablet, UserPlus, Repeat, Flame, Layers, Clock, MoveVertical, Bot, Database, Moon, Info } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { api } from "@/lib/api";

const RANGES = [
  { label: "7 giorni", value: 7 },
  { label: "30 giorni", value: 30 },
  { label: "90 giorni", value: 90 },
];

const fmt = (n) => new Intl.NumberFormat("it-IT").format(n ?? 0);
const fmtTime = (s) => { s = Math.round(s || 0); const m = Math.floor(s / 60); const r = s % 60; return m > 0 ? `${m}m ${r}s` : `${r}s`; };
const dayLabel = (d) => { const [, m, day] = d.split("-"); return `${day}/${m}`; };

const Growth = ({ v }) => {
  if (v == null) return null;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-[#166534]" : "text-[#B91C1C]"}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{Math.abs(v)}%
    </span>
  );
};

const Hint = ({ text }) => (
  <span className="group relative inline-flex items-center align-middle" data-testid="hint">
    <Info className="w-3.5 h-3.5 text-[#A9B4C1] hover:text-[#2472C8] cursor-help ml-1" />
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-60 rounded-lg bg-[#1B2A41] text-white text-xs leading-relaxed px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-lg">
      {text}
    </span>
  </span>
);

const Bars = ({ items }) => {
  const total = items.reduce((s, i) => s + i.value, 0) || 1;
  return (
    <div className="space-y-3 mt-2">
      {items.map((it) => (
        <div key={it.label} data-testid={`bar-${it.label}`}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="flex items-center gap-2 text-[#1B2A41]">{it.icon}{it.label}</span>
            <span className="text-[#566476] font-medium">{fmt(it.value)} · {Math.round(it.value / total * 100)}%</span>
          </div>
          <div className="h-2 rounded-full bg-[#EEF4F9] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${it.value / total * 100}%`, background: it.color }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const Panel = ({ title, hint, children }) => (
  <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6">
    <h2 className="font-heading font-semibold text-[#1B2A41] text-lg mb-2 flex items-center">{title}{hint && <Hint text={hint} />}</h2>
    {children}
  </div>
);

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ["analytics", days],
    queryFn: async () => (await api.get(`/admin/analytics?days=${days}`)).data,
  });

  const daily = (data?.daily || []).map((d) => ({ ...d, label: dayLabel(d.day) }));
  const hourly = (data?.hourly || []).map((h) => ({ ...h, label: String(h.hour).padStart(2, "0") }));
  const dev = data?.devices || { desktop: 0, mobile: 0, tablet: 0 };
  const topBots = data?.top_bots || [];
  const maxBot = Math.max(1, ...topBots.map((b) => b.views));

  const cards = [
    { label: "Visite totali", value: data?.total_views, growth: data?.views_growth, icon: Eye },
    { label: "Visitatori unici", value: data?.unique_visitors, growth: data?.visitors_growth, icon: Users },
    { label: "Pagine per visita", value: data?.avg_pages_per_visitor, sub: "media", icon: Layers },
    { label: "Tempo medio su pagina", display: fmtTime(data?.avg_time_on_page), sub: "permanenza", icon: Clock },
    { label: "Scroll medio", display: `${data?.avg_scroll_pct ?? 0}%`, sub: "profondità lettura", icon: MoveVertical },
    { label: "Visite oggi", value: data?.today_views, sub: "dalla mezzanotte", icon: CalendarDays },
  ];

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#1B2A41]">Statistiche del sito</h1>
          <p className="text-[#566476] mt-1 max-w-2xl">Dati interni e completi: contano ogni persona reale, <strong>anche chi rifiuta i cookie</strong> (a differenza di Google Analytics). Nessun cookie di terze parti, conforme GDPR.</p>
        </div>
        <div className="flex gap-1 bg-white border border-[#DCE6EF] rounded-xl p-1">
          {RANGES.map((r) => (
            <button key={r.value} onClick={() => setDays(r.value)} data-testid={`analytics-range-${r.value}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${days === r.value ? "bg-[#2472C8] text-white" : "text-[#566476] hover:bg-[#EEF4F9]"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-6 border border-[#DCE6EF]" data-testid={`analytics-stat-${c.label}`}>
            <div className="flex items-center justify-between">
              <c.icon className="w-6 h-6 text-[#2472C8]" />
              {c.growth !== undefined && <Growth v={c.growth} />}
            </div>
            <div className="font-mono text-4xl font-semibold text-[#1B2A41] mt-4">{isLoading ? "…" : (c.display ?? fmt(c.value))}</div>
            <div className="text-sm font-medium text-[#1B2A41] mt-1">{c.label}</div>
            <div className="text-xs text-[#8695A6]">{c.sub || (c.growth !== undefined ? `vs ${data?.days ?? days} gg precedenti` : "")}</div>
          </div>
        ))}
      </div>

      {/* Visitatori: nuovi / di ritorno / abituali + base storica */}
      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <Panel title="Tipo di visitatori" hint="Calcolato sul periodo selezionato. Nuovi = prima visita in assoluto. Di ritorno = già conosciuti prima e tornati ora. Abituali = tornati 2+ volte nel periodo.">
          <Bars items={[
            { label: "Nuovi", value: data?.new_visitors ?? 0, color: "#2472C8", icon: <UserPlus className="w-4 h-4 text-[#2472C8]" /> },
            { label: "Di ritorno", value: data?.returning_visitors ?? 0, color: "#F5A623", icon: <Repeat className="w-4 h-4 text-[#F5A623]" /> },
            { label: "Abituali", value: data?.habitual_visitors ?? 0, color: "#B4531F", icon: <Flame className="w-4 h-4 text-[#B4531F]" /> },
          ]} />
          <p className="text-xs text-[#8695A6] mt-4 leading-relaxed">Gli "Abituali" sono un sottoinsieme: visitatori che hanno aperto il sito più di una volta in questi {data?.days ?? days} giorni.</p>
        </Panel>

        <Panel title="Base storica" hint="Numeri su TUTTA la storia del sito, non solo sul periodo. I 'dormienti' sono visitatori conosciuti in passato che non sono tornati nel periodo selezionato.">
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="rounded-xl bg-[#EEF4F9] p-4" data-testid="stat-total-known">
              <Database className="w-5 h-5 text-[#2472C8]" />
              <div className="font-mono text-3xl font-semibold text-[#1B2A41] mt-2">{isLoading ? "…" : fmt(data?.total_known_visitors)}</div>
              <div className="text-xs text-[#566476] mt-0.5">Visitatori totali di sempre</div>
            </div>
            <div className="rounded-xl bg-[#FBF3E7] p-4" data-testid="stat-dormant">
              <Moon className="w-5 h-5 text-[#B4531F]" />
              <div className="font-mono text-3xl font-semibold text-[#1B2A41] mt-2">{isLoading ? "…" : fmt(data?.dormant_visitors)}</div>
              <div className="text-xs text-[#566476] mt-0.5">Dormienti (non tornati)</div>
            </div>
          </div>
          <p className="text-xs text-[#8695A6] mt-4 leading-relaxed">I dormienti sono potenziali clienti già interessati in passato: un'occasione per campagne di richiamo.</p>
        </Panel>

        <Panel title="Dispositivi">
          <Bars items={[
            { label: "Desktop", value: dev.desktop, color: "#2472C8", icon: <Monitor className="w-4 h-4 text-[#2472C8]" /> },
            { label: "Mobile", value: dev.mobile, color: "#F5A623", icon: <Smartphone className="w-4 h-4 text-[#F5A623]" /> },
            { label: "Tablet", value: dev.tablet, color: "#166534", icon: <Tablet className="w-4 h-4 text-[#166534]" /> },
          ]} />
        </Panel>
      </div>

      <div className="bg-white rounded-2xl border border-[#DCE6EF] mt-8 p-6">
        <h2 className="font-heading font-semibold text-[#1B2A41] text-lg mb-6">Andamento visite</h2>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2472C8" stopOpacity={0.35} /><stop offset="95%" stopColor="#2472C8" stopOpacity={0} /></linearGradient>
                <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F5A623" stopOpacity={0.35} /><stop offset="95%" stopColor="#F5A623" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAF0F6" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8695A6" }} interval="preserveStartEnd" minTickGap={20} />
              <YAxis tick={{ fontSize: 11, fill: "#8695A6" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #DCE6EF", fontSize: 13 }} formatter={(v, name) => [fmt(v), name === "views" ? "Visite" : "Visitatori"]} labelFormatter={(l) => `Giorno ${l}`} />
              <Area type="monotone" dataKey="views" stroke="#2472C8" strokeWidth={2} fill="url(#gViews)" />
              <Area type="monotone" dataKey="visitors" stroke="#F5A623" strokeWidth={2} fill="url(#gVisitors)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-6 mt-4 text-xs text-[#566476]">
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#2472C8]" /> Visite</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#F5A623]" /> Visitatori</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <Panel title="Giorni della settimana">
          <div className="h-44 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.weekday || []} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAF0F6" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#8695A6" }} />
                <YAxis tick={{ fontSize: 11, fill: "#8695A6" }} allowDecimals={false} width={28} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #DCE6EF", fontSize: 13 }} formatter={(v) => [fmt(v), "Visite"]} />
                <Bar dataKey="views" radius={[6, 6, 0, 0]}>
                  {(data?.weekday || []).map((_, i) => <Cell key={i} fill={i >= 5 ? "#F5A623" : "#2472C8"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Fasce orarie" hint="Orario UTC. Mostra in quali ore del giorno il sito riceve più visite.">
          <div className="h-44 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAF0F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8695A6" }} interval={1} />
                <YAxis tick={{ fontSize: 11, fill: "#8695A6" }} allowDecimals={false} width={28} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #DCE6EF", fontSize: 13 }} formatter={(v) => [fmt(v), "Visite"]} labelFormatter={(l) => `Ore ${l}:00`} />
                <Bar dataKey="views" radius={[4, 4, 0, 0]} fill="#2472C8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      {/* Bot & crawler dedicato */}
      <div className="bg-white rounded-2xl border border-[#DCE6EF] mt-8 p-6" data-testid="analytics-bots">
        <h2 className="font-heading font-semibold text-[#1B2A41] text-lg mb-1 flex items-center gap-2"><Bot className="w-5 h-5 text-[#2472C8]" /> Bot e crawler</h2>
        <p className="text-sm text-[#566476] max-w-3xl leading-relaxed">
          Sono visite automatiche di robot, non persone reali: motori di ricerca (Googlebot), anteprime dei link condivisi su WhatsApp/Facebook, scanner SEO e servizi di monitoraggio.
          Sono <strong>escluse</strong> da tutte le statistiche qui sopra; le mostriamo a parte perché indicano che il sito viene indicizzato e letto dai motori.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          <div className="rounded-xl bg-[#EEF4F9] p-4">
            <div className="font-mono text-3xl font-semibold text-[#1B2A41]">{isLoading ? "…" : fmt(data?.bot_views)}</div>
            <div className="text-xs text-[#566476] mt-0.5">Accessi bot nel periodo</div>
          </div>
          <div className="rounded-xl bg-[#EEF4F9] p-4">
            <div className="font-mono text-3xl font-semibold text-[#1B2A41]">{isLoading ? "…" : fmt(data?.bot_visitors)}</div>
            <div className="text-xs text-[#566476] mt-0.5">Bot distinti</div>
          </div>
          <div className="lg:row-span-1">
            {topBots.length ? (
              <div className="space-y-2.5">
                {topBots.map((b) => (
                  <div key={b.name} data-testid={`bot-${b.name}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-[#1B2A41]">{b.name}</span>
                      <span className="text-[#566476] font-medium">{fmt(b.views)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#EEF4F9] overflow-hidden">
                      <div className="h-full rounded-full bg-[#8695A6]" style={{ width: `${b.views / maxBot * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="text-sm text-[#8695A6]">Nessun bot registrato nel periodo.</div>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-white rounded-2xl border border-[#DCE6EF]">
          <div className="p-6 border-b border-[#EAF0F6]"><h2 className="font-heading font-semibold text-[#1B2A41] text-lg">Pagine più viste</h2></div>
          {data?.top_pages?.length ? (
            <div className="divide-y divide-[#EAF0F6]">
              {data.top_pages.map((p) => (
                <div key={p.path} className="p-4 sm:px-6 flex items-center justify-between gap-4" data-testid="analytics-top-page">
                  <span className="text-sm text-[#1B2A41] truncate font-mono">{p.path}</span>
                  <span className="text-sm font-semibold text-[#2472C8] shrink-0">{fmt(p.views)}</span>
                </div>
              ))}
            </div>
          ) : <div className="p-8 text-center text-[#8695A6]">Nessun dato ancora.</div>}
        </div>

        <div className="bg-white rounded-2xl border border-[#DCE6EF]">
          <div className="p-6 border-b border-[#EAF0F6]"><h2 className="font-heading font-semibold text-[#1B2A41] text-lg">Provenienza (referrer)</h2></div>
          {data?.top_referrers?.length ? (
            <div className="divide-y divide-[#EAF0F6]">
              {data.top_referrers.map((r) => (
                <div key={r.referrer} className="p-4 sm:px-6 flex items-center justify-between gap-4" data-testid="analytics-top-referrer">
                  <span className="text-sm text-[#1B2A41] truncate">{r.referrer}</span>
                  <span className="text-sm font-semibold text-[#2472C8] shrink-0">{fmt(r.views)}</span>
                </div>
              ))}
            </div>
          ) : <div className="p-8 text-center text-[#8695A6]">Nessun referrer esterno registrato.</div>}
        </div>
      </div>

      <p className="text-xs text-[#8695A6] mt-6">
        Queste statistiche interne sono la fonte più completa (contano tutti). Google Analytics, attivo solo previo consenso cookie, resta disponibile come analisi aggiuntiva.
      </p>
    </div>
  );
}
